import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { metricDetailApi, managementApi } from '../../services/api';
import { formatPercent, formatMs, formatNumber } from '../../utils/formatters';
import AnomalyTrendChart from './AnomalyTrendChart';
import MetricBreakdownTable from './MetricBreakdownTable';
import RecommendationPanel from './RecommendationPanel';
import LoadingSpinner from '../common/LoadingSpinner';

const METRIC_CONFIG = {
  'health-score': {
    label: 'Health Score', unit: 'score', color: '#6366f1', higherIsBetter: true,
    format: (v) => Number(v).toFixed(1),
    description: 'Composite health score (0-100) combining crash rate, ANR, startup time, rendering, and frozen frames.',
    benchmarks: { good: 70, target: 85 },
  },
  'crash-free': {
    label: 'Crash-Free Users', unit: '%', color: '#10b981', higherIsBetter: true,
    format: (v) => formatPercent(v),
    description: 'Percentage of users who did not experience a crash. Google Play threshold: 98.91%.',
    benchmarks: { googlePlay: 98.91, good: 99.5 },
  },
  'anr-rate': {
    label: 'ANR Rate', unit: '%', color: '#ef4444', higherIsBetter: false,
    format: (v) => formatPercent(v, 3),
    description: 'Application Not Responding rate. Google Play threshold: 0.47%.',
    benchmarks: { googlePlay: 0.47, good: 0.20 },
  },
  'cold-start': {
    label: 'Cold Start P90', unit: 'ms', color: '#f59e0b', higherIsBetter: false,
    format: (v) => formatMs(v),
    description: '90th percentile cold start time. Target: under 4 seconds.',
    benchmarks: { target: 4000, good: 2000 },
  },
  'slow-rendering': {
    label: 'Slow Rendering', unit: '%', color: '#8b5cf6', higherIsBetter: false,
    format: (v) => formatPercent(v),
    description: 'Percentage of frames taking >16ms to render.',
    benchmarks: { target: 15, good: 5 },
  },
  'frozen-frames': {
    label: 'Frozen Frames', unit: '%', color: '#ec4899', higherIsBetter: false,
    format: (v) => formatPercent(v),
    description: 'Percentage of frames taking >700ms to render (complete UI freezes).',
    benchmarks: { target: 5, good: 1 },
  },
  'sessions': {
    label: 'Sessions', unit: '', color: '#06b6d4', higherIsBetter: true,
    format: (v) => formatNumber(v),
    description: 'Total app sessions across all users and versions.',
    benchmarks: null,
  },
};

const PERIODS = [
  { key: 'daily', label: '7D', days: 7 },
  { key: 'daily', label: '30D', days: 30 },
  { key: 'daily', label: '90D', days: 90 },
  { key: 'weekly', label: 'Weekly', days: 90 },
  { key: 'monthly', label: 'Monthly', days: 365 },
];

const PLATFORM_COLORS = {
  mobile: '#3b82f6',
  atv: '#10b981',
  firestick: '#f97316',
};

const PLATFORM_LABELS = {
  mobile: 'Mobile',
  atv: 'Android TV',
  firestick: 'FireStick',
};

// Map metric keys to platform_daily_metrics columns
const PLATFORM_METRIC_COL = {
  'health-score': 'health_score',
  'crash-free': 'crash_free_users_pct',
  'anr-rate': 'anr_rate',
  'cold-start': 'cold_start_p90',
  'slow-rendering': 'slow_rendering_pct',
  'frozen-frames': 'frozen_frames_pct',
  'sessions': 'total_sessions',
};

const TABS = [
  { key: 'trend', label: 'Trend Analysis' },
  { key: 'platform', label: 'Platform Breakdown' },
  { key: 'breakdown', label: 'Version & Device' },
  { key: 'recommendations', label: 'Recommendations' },
];

const PlatformBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-surface-700 border border-surface-500 rounded-lg p-3 shadow-xl text-xs">
      <p className="text-gray-300 font-medium mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} style={{ color: e.fill || e.color }} className="flex justify-between gap-4">
          <span>{PLATFORM_LABELS[e.dataKey] || e.dataKey}:</span>
          <span className="font-mono font-semibold">{typeof e.value === 'number' ? e.value.toFixed(2) : e.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function MetricDetailPage() {
  const { metricKey } = useParams();
  const config = METRIC_CONFIG[metricKey];

  const [trendData, setTrendData] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [platformData, setPlatformData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePeriod, setActivePeriod] = useState(1); // index into PERIODS
  const [activeTab, setActiveTab] = useState('trend');

  useEffect(() => {
    if (!config) return;
    loadData();
  }, [metricKey, activePeriod]);

  const loadData = async () => {
    setLoading(true);
    try {
      const period = PERIODS[activePeriod];
      const promises = [
        metricDetailApi.getTrend(metricKey, period.days, period.key),
        metricDetailApi.getBreakdown(metricKey),
        metricDetailApi.getRecommendations(metricKey),
      ];

      // Also load platform data
      promises.push(
        managementApi.getPlatformSummary().catch(() => ({ data: { platforms: [] } }))
      );

      const [trendRes, breakdownRes, recsRes, platformRes] = await Promise.all(promises);
      setTrendData(trendRes.data);
      setBreakdownData(breakdownRes.data);
      setRecommendations(recsRes.data.recommendations);
      setPlatformData(platformRes.data.platforms || []);
    } catch (err) {
      console.error('Failed to load metric detail:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400 text-lg">Unknown metric: {metricKey}</p>
        <Link to="/management" className="text-brand-400 hover:underline mt-2 inline-block">Back to Dashboard</Link>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message={`Loading ${config.label} analytics...`} />;

  // Version breakdown columns
  const versionColumns = [
    { key: 'app_version', label: 'Version' },
    { key: 'value', label: config.label, render: (v) => config.format(v) },
    { key: 'sessions', label: 'Sessions', render: (v) => formatNumber(v) },
    { key: 'users', label: 'Users', render: (v) => formatNumber(v) },
  ];

  // Device breakdown columns
  const deviceColumns = [
    { key: 'device_type', label: 'Device Type', render: (v) => (v || '').replace('_', ' ').toUpperCase() },
    { key: 'model_count', label: 'Models', render: (v) => formatNumber(v) },
    { key: 'total_sessions', label: 'Sessions', render: (v) => formatNumber(v) },
    { key: 'total_crashes', label: 'Crashes', render: (v) => formatNumber(v) },
    { key: 'total_anrs', label: 'ANRs', render: (v) => formatNumber(v) },
  ];

  // Top devices columns
  const topDeviceColumns = [
    { key: 'device_model', label: 'Device' },
    { key: 'device_type', label: 'Type', render: (v) => (v || '').replace('_', ' ') },
    { key: 'os_version', label: 'OS' },
    { key: 'crash_count', label: 'Crashes', render: (v) => formatNumber(v) },
    { key: 'anr_count', label: 'ANRs', render: (v) => formatNumber(v) },
    { key: 'session_count', label: 'Sessions', render: (v) => formatNumber(v) },
    { key: 'avg_crash_rate', label: 'Crash Rate/10k', render: (v) => Number(v).toFixed(2) },
  ];

  // Platform breakdown data for bar chart
  const platformCol = PLATFORM_METRIC_COL[metricKey];
  const platformChartData = platformData.map(p => ({
    platform: PLATFORM_LABELS[p.platform] || p.platform,
    value: parseFloat(p[platformCol]) || 0,
    key: p.platform,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/management" className="text-gray-500 hover:text-gray-300 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-bold text-gray-100">{config.label} — Deep Dive</h1>
          </div>
          <p className="text-sm text-gray-500 ml-7">{config.description}</p>
        </div>

        {/* Benchmark tooltip */}
        {config.benchmarks && (
          <div className="hidden md:block text-right">
            <div className="text-[10px] text-gray-600 uppercase tracking-wide mb-1">Industry Benchmarks</div>
            <div className="flex gap-3 text-xs">
              {config.benchmarks.googlePlay != null && (
                <span className="bg-red-500/10 text-red-400 px-2 py-1 rounded">
                  Google Play: {config.benchmarks.googlePlay}{config.unit === '%' ? '%' : ''}
                </span>
              )}
              {config.benchmarks.target != null && (
                <span className="bg-amber-500/10 text-amber-400 px-2 py-1 rounded">
                  Target: {config.benchmarks.target}{config.unit === '%' ? '%' : config.unit === 'ms' ? 'ms' : ''}
                </span>
              )}
              {config.benchmarks.good != null && (
                <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded">
                  Good: {config.benchmarks.good}{config.unit === '%' ? '%' : config.unit === 'ms' ? 'ms' : ''}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-surface-700 pb-0">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-[1px] ${
              activeTab === tab.key
                ? 'border-brand-500 text-brand-400'
                : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-surface-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Trend Tab */}
      {activeTab === 'trend' && (
        <>
          {/* Period Selector */}
          <div className="flex gap-2">
            {PERIODS.map((p, i) => (
              <button
                key={i}
                onClick={() => setActivePeriod(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activePeriod === i
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-700 text-gray-400 hover:text-gray-200 hover:bg-surface-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Trend Chart with Anomaly Highlighting */}
          <div>
            <h2 className="text-sm font-semibold text-gray-300 mb-2">Trend Analysis</h2>
            <AnomalyTrendChart
              data={trendData?.trend}
              label={config.label}
              color={config.color}
              thresholds={trendData?.thresholds}
              anomalies={trendData?.anomalies}
              unit={config.unit === '%' ? '%' : ''}
              higherIsBetter={config.higherIsBetter}
            />
          </div>
        </>
      )}

      {/* Platform Tab */}
      {activeTab === 'platform' && (
        <div className="space-y-6">
          {platformChartData.length > 0 ? (
            <>
              {/* Platform bar chart */}
              <div className="card">
                <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase">
                  {config.label} by Platform
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={platformChartData} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2e3354" vertical={false} />
                    <XAxis dataKey="platform" tick={{ fill: '#9ca3af', fontSize: 12 }} tickLine={false} />
                    <YAxis
                      tick={{ fill: '#9ca3af', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={v => config.unit === '%' ? `${v}%` : config.unit === 'ms' ? `${v}ms` : v}
                    />
                    <RechartsTooltip content={<PlatformBarTooltip />} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={50}>
                      {platformChartData.map((entry, i) => (
                        <Cell key={i} fill={PLATFORM_COLORS[entry.key] || '#6366f1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Platform comparison table */}
              <div className="card">
                <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Platform Details</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-600">
                        <th className="table-header text-left py-3 px-4">Platform</th>
                        <th className="table-header text-right py-3 px-4">{config.label}</th>
                        <th className="table-header text-right py-3 px-4">Crash-Free %</th>
                        <th className="table-header text-right py-3 px-4">ANR Rate</th>
                        <th className="table-header text-right py-3 px-4">Cold Start P90</th>
                        <th className="table-header text-right py-3 px-4">Health Score</th>
                        <th className="table-header text-right py-3 px-4">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platformData.map(p => {
                        const hs = parseFloat(p.health_score) || 0;
                        return (
                          <tr key={p.platform} className="border-b border-surface-700 hover:bg-surface-700/30 transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-3 h-3 rounded-full inline-block"
                                  style={{ backgroundColor: PLATFORM_COLORS[p.platform] || '#6366f1' }}
                                />
                                <span className="font-medium text-gray-300">
                                  {PLATFORM_LABELS[p.platform] || p.platform}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-gray-200">
                              {config.format(p[platformCol] || 0)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-gray-300">
                              {formatPercent(p.crash_free_users_pct)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-gray-300">
                              {formatPercent(p.anr_rate, 3)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-gray-300">
                              {formatMs(p.cold_start_p90)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`font-mono font-semibold ${
                                hs >= 70 ? 'text-emerald-400' : hs >= 40 ? 'text-amber-400' : 'text-red-400'
                              }`}>
                                {hs.toFixed(1)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-gray-400">
                              {formatNumber(p.total_users)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="card text-center py-10 text-gray-500">
              <p className="text-lg mb-2">No platform data available</p>
              <p className="text-sm">Run a data refresh with <code className="bg-surface-700 px-2 py-0.5 rounded text-xs">BQ_APP_VARIANTS=mobile,atv,firestick</code> to populate platform metrics.</p>
            </div>
          )}
        </div>
      )}

      {/* Version & Device Breakdown Tab */}
      {activeTab === 'breakdown' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <MetricBreakdownTable
              title="By App Version"
              data={breakdownData?.byVersion}
              columns={versionColumns}
            />
            <MetricBreakdownTable
              title="By Device Type"
              data={breakdownData?.byDevice}
              columns={deviceColumns}
            />
          </div>

          {/* Top Devices */}
          {breakdownData?.topDevices?.length > 0 && (
            <MetricBreakdownTable
              title="Top Devices by Crash Rate"
              data={breakdownData.topDevices}
              columns={topDeviceColumns}
            />
          )}
        </>
      )}

      {/* Recommendations Tab */}
      {activeTab === 'recommendations' && (
        <RecommendationPanel recommendations={recommendations} />
      )}
    </div>
  );
}
